/**
 * Server-Sent-Events consumption for the streamed endpoints (architecture
 * §6: today that means the interview, and only the interview — refine went
 * back to a plain JSON POST once it became one structured-output call with
 * nothing to stream). `EventSource` cannot attach an
 * `Authorization` header, so streaming goes through `fetch` + a manual
 * reader instead — the one place that parses the `event:`/`data:` framing.
 *
 * Cancellation rides in on `init.signal`, which reaches `fetch` unchanged. On
 * abort the pending `reader.read()` rejects, the `finally` releases the lock,
 * and the consumer's `for await` throws — so a caller that aborts on unmount
 * must let `isAbortError` rejections through instead of painting an error.
 * Abandoning the generator (`break`) runs the `finally` but leaves the request
 * open; only a signal actually closes the connection.
 */

import { apiErrorFrom, ApiError } from './client'
import { getToken } from '../auth/session'

export interface SseEvent {
  event: string
  data: string
}

export async function* streamSse(path: string, init: RequestInit = {}): AsyncGenerator<SseEvent> {
  const token = getToken()
  const headers = new Headers(init.headers)
  headers.set('Accept', 'text/event-stream')
  if (init.body !== undefined && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const res = await fetch(`/api${path}`, { ...init, headers })
  // Same error shape as the JSON client, including the 401 path: a streamed
  // route that rejects a stale token has to end the session like any other, and
  // callers get the backend's own message instead of a bare status line.
  if (!res.ok) throw await apiErrorFrom(res)
  if (!res.body) throw new ApiError('The server sent no stream body', res.status)

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      let boundary = buffer.indexOf('\n\n')
      while (boundary !== -1) {
        const block = buffer.slice(0, boundary)
        buffer = buffer.slice(boundary + 2)
        const parsed = parseSseBlock(block)
        if (parsed) yield parsed
        boundary = buffer.indexOf('\n\n')
      }
    }
  } finally {
    reader.releaseLock()
  }
}

function parseSseBlock(block: string): SseEvent | null {
  let event = 'message'
  const dataLines: string[] = []
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) event = line.slice('event:'.length).trim()
    else if (line.startsWith('data:')) dataLines.push(line.slice('data:'.length).trim())
  }
  if (dataLines.length === 0) return null
  return { event, data: dataLines.join('\n') }
}
